// How the personal queue is filtered and ordered.
//
// These are lead-queue domain rules, not generic helpers: what "reachable"
// means and how the contact-info filter reads. They lived in components/utils.ts,
// which half the app imported, so the queue's own vocabulary was buried in a
// file named after neither leads nor the queue.
//
// The score jitter used to live here too, mirroring the server's ranking so the
// client's re-sort could not disagree with it. CRM-84 removed the score, and
// with it the only thing the two sides had to agree about beyond the bands.

import { BAND, CLOSED_STAGES, EXCLUDED_SF_STATUSES } from '@/shared/domain'
import type { LeadRow } from '@/shared/types'

// True when the lead has neither an email address nor a phone number — the
// BDR has nothing to dial or write. Used by the contact-info filter in both
// Table view and Focus mode (and any future Excel exporter for "leads we
// couldn't find contacts for"). Single definition so the predicate stays
// consistent everywhere.
export function isUnreachable(l: { email?: string; phone?: string }): boolean {
  const e = (l.email || '').trim()
  const p = (l.phone || '').trim()
  return !e && !p
}

// Three-state contact-info filter, shared between Table and Focus views.
//   'all'         → no filtering on contact info
//   'reachable'   → hide leads with no email and no phone
//   'unreachable' → show ONLY leads with no contact info
export type ContactInfoFilter = 'all' | 'reachable' | 'unreachable'

export function isValidContactInfoFilter(v: unknown): v is ContactInfoFilter {
  return v === 'all' || v === 'reachable' || v === 'unreachable'
}

export function bandOf(l: LeadRow, now: number): number {
  if (!l.nextContactAt) return BAND.FRESH
  const time = Date.parse(l.nextContactAt)
  if (Number.isNaN(time)) return BAND.FRESH
  if (time <= now) return BAND.DUE
  const endOfDay = new Date(now)
  endOfDay.setHours(23, 59, 59, 999)
  return time <= endOfDay.getTime() ? BAND.SOON : BAND.WAITING
}

/**
 * The band a Focus card sorts and filters in. The server's `queueBand` wins,
 * because the server draws the day boundary in Stockholm time and the browser's
 * clock may not. The local rule decides only when there is no server band,
 * which is the case after an optimistic edit (mergeFields drops it). One
 * override: a promise whose moment has passed is due, whatever the cached
 * snapshot said. That comparison does not depend on a timezone, and without it
 * a callback would wait for the next poll to surface.
 */
export function queueBandOf(l: LeadRow, now: number): number {
  const local = bandOf(l, now)
  if (l.queueBand == null || local === BAND.DUE) return local
  return l.queueBand
}

const closedStages = new Set<string>(CLOSED_STAGES)
const excludedSf = new Set<string>(EXCLUDED_SF_STATUSES)

/** Cards and contact-filter badges must use exactly the same visible book. */
export function focusQueueBase(leads: LeadRow[], showAll: boolean, now: number): LeadRow[] {
  return leads.filter(l => {
    if (closedStages.has(l.stage)) return false
    if (l.sfStatus && excludedSf.has(l.sfStatus)) return false
    if (!showAll && (l.skipped || queueBandOf(l, now) === BAND.WAITING)) return false
    return true
  })
}
